import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { useApiFallback } from "../../hooks/useApiFallback";
import { listEvents } from "../../api/endpoints";
import { toLogEvent } from "../../api/adapters";
import { NODE_INFO, nodeLog, type NodeLogLevel, type LogEvent } from "../../data";
import { downloadText, toCsv } from "../../utils/download";
import { useToast } from "../../components/Toast";

// На бэке нет «локального» журнала отдельно — используем общий /events с фильтром
// по `type`. Для удобства быстрых табов мапим UI-фильтр в backend `type`.
type Filt = "all" | "install" | "download" | "revoke" | "sync" | "login" | "errors";
const PAGE_SIZE = 100;

const FILT_TO_TYPE: Record<Filt, string | undefined> = {
  all: undefined,
  install: "install",
  download: "download",
  revoke: "revoke",
  sync: "sync",
  login: "login",
  errors: undefined, // нет отдельного типа — фильтруем по цвету (red/amber) на клиенте
};

// Цвет события из EventOut → уровень для UI-чипа.
function levelFromColor(color: string): NodeLogLevel {
  if (color === "red") return "err";
  if (color === "amber") return "warn";
  return "info";
}

export default function LogPage() {
  const push = useToast();
  const [filt, setFilt] = useState<Filt>("all");

  const typeParam = FILT_TO_TYPE[filt];

  const eventsQ = useQuery({
    queryKey: ["local", "events", { type: typeParam }],
    queryFn: () => listEvents({ type: typeParam, page: 1, page_size: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  // Fallback — используем mock node-log в форме LogEvent.
  const fallbackEvents = useMemo<LogEvent[]>(
    () =>
      nodeLog.map((e) => ({
        id: e.id,
        color: e.level === "err" ? "red" : e.level === "warn" ? "amber" : "blue",
        who: e.source,
        text: e.text,
        detail: "",
        when: "",
        time: e.time,
        type: e.source === "syncd" ? "sync" : "install",
        source: e.source,
        target: e.target,
      })),
    [],
  );

  const apiEvents = eventsQ.data?.items.map(toLogEvent);
  const events = useApiFallback<LogEvent[]>(apiEvents, eventsQ.isError, fallbackEvents);

  // На клиенте дополнительно фильтруем «errors» по цвету.
  const filtered = useMemo(() => {
    let list = events.value;
    if (filt === "errors") list = list.filter((e) => e.color === "red" || e.color === "amber");
    return list;
  }, [events.value, filt]);

  const total = eventsQ.data?.meta.total ?? filtered.length;

  function exportCsv() {
    downloadText(
      `astramarket-${NODE_INFO.id}-events-${new Date().toISOString().slice(0, 10)}.csv`,
      "\ufeff" + toCsv([
        ["ID", "Время", "Уровень", "Источник", "Объект", "Событие"],
        ...filtered.map((event) => [
          event.id,
          event.time,
          levelFromColor(event.color),
          event.source,
          event.target,
          event.text,
        ]),
      ]),
      "text/csv;charset=utf-8",
    );
    push({ kind: "success", text: `Экспортировано событий: ${filtered.length}` });
  }

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Журнал событий узла
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Журналы передаются на Глобал. Подписаны узлом, изменение невозможно.
          </div>
        </div>
        <div className="flex gap-2">
          <Btn icon={Download} onClick={exportCsv}>Экспорт CSV</Btn>
        </div>
      </header>

      <div className="-mx-1 overflow-x-auto">
        <div className="flex min-w-max gap-1 px-1">
          <Pill v="all"      f={filt} on={setFilt}>все события</Pill>
          <Pill v="install"  f={filt} on={setFilt}>установка</Pill>
          <Pill v="download" f={filt} on={setFilt}>скачивание</Pill>
          <Pill v="revoke"   f={filt} on={setFilt}>отзыв</Pill>
          <Pill v="sync"     f={filt} on={setFilt}>синхронизация</Pill>
          <Pill v="login"    f={filt} on={setFilt}>вход</Pill>
          <Pill v="errors"   f={filt} on={setFilt}>ошибки</Pill>
        </div>
      </div>

      <section className="mt-3 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          <span>
            journal · {filtered.length} / {total.toLocaleString("ru-RU")} записей
            {eventsQ.isFetching && " · обновляется…"}
          </span>
          <span>signed by node <span className="font-mono">{NODE_INFO.id}</span></span>
        </div>
        <div className="-mx-1 overflow-x-auto">
          <table className="w-full min-w-[720px] text-[12.5px]">
            <tbody>
              {filtered.map((e, i) => (
                <tr
                  key={e.id}
                  style={{ animationDelay: `${Math.min(i, 10) * 25}ms` }}
                  className="stagger-up border-t border-[var(--color-border)] first:border-0 transition-colors hover:bg-[var(--color-surface-2)]"
                >
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11.5px] text-[var(--color-fg-muted)]">{e.time}</td>
                  <td className="whitespace-nowrap px-3 py-2"><LevelChip level={levelFromColor(e.color)} /></td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[12px] text-[var(--color-fg)]">{e.source}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11.5px] text-[var(--color-fg-muted)]">{e.target}</td>
                  <td className="px-3 py-2 text-[var(--color-fg)]">{e.text}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[11px] text-[var(--color-fg-faint)]">{e.id}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-[12.5px] text-[var(--color-fg-muted)]">
                  Нет событий по этому фильтру.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function Pill({ v, f, on, children }: { v: Filt; f: Filt; on: (v: Filt) => void; children: React.ReactNode }) {
  const active = f === v;
  return (
    <button
      onClick={() => on(v)}
      className={
        "rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors " +
        (active
          ? "bg-[var(--color-brand)] text-white"
          : "bg-[var(--color-surface-2)] text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-fg)]")
      }
    >
      {children}
    </button>
  );
}

function LevelChip({ level }: { level: NodeLogLevel }) {
  const map = {
    info: { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)", text: "info" },
    warn: { bg: "var(--color-warning-bg)", fg: "var(--color-warning)", text: "warn" },
    err:  { bg: "var(--color-danger-bg)",  fg: "var(--color-danger)",  text: "err" },
  };
  const m = map[level];
  return (
    <span
      className="rounded px-1.5 py-0.5 font-mono text-[10px] font-medium"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.text}
    </span>
  );
}
